import { FC, useEffect, useMemo, useState } from 'react';
import { FormProvider, useForm } from 'react-hook-form';

import { usePerformTransitionAndNavigate } from '~/core/application/hooks';
import { handleValidationErrorResponse } from '~/core/shared/api';
import { useUpdateOrCreateDocument } from '~/core/shared/api/documents/hooks';
import { useStepComponentsContext } from '~/core/shared/contexts/StepComponentsContext';
import { useFormBlocks, useFormResolver } from '~/core/shared/hooks';
import { Entity, WorkflowDefaultStep } from '~/core/shared/types';
import { formatFormValuesForApi } from '~/core/shared/utils/forms';
import { useDefaultValuesForFormFields } from '../hooks/workflowFormFields';

type DefaultStepContainerProps = {
  step: WorkflowDefaultStep;
};

export const DefaultStepContainer: FC<DefaultStepContainerProps> = ({
  step,
}) => {
  const { updateOrCreateDocument } = useUpdateOrCreateDocument();
  const performTransitionAndNavigate = usePerformTransitionAndNavigate();
  const { StepRenderer } = useStepComponentsContext();
  const [isProcessing, setIsProcessing] = useState<boolean>(false);
  const { blocks, documentPath: _documentPath = [], documentDefinition } = step;

  const documentPath = useMemo(
    () =>
      typeof _documentPath === 'function' ? _documentPath() : _documentPath,
    [_documentPath],
  );
  const formFieldBlocks = useFormBlocks(step.blocks);
  const defaultValues = useDefaultValuesForFormFields(
    formFieldBlocks,
    documentPath,
  );
  const resolver = useFormResolver([{ formFieldBlocks }]);
  const form = useForm<Record<string, any>>({
    defaultValues,
    resolver,
  });

  useEffect(() => {
    setIsProcessing(false);
    form.reset(defaultValues);
  }, [step]);

  const onSubmit = async (formValues: Entity): Promise<void> => {
    setIsProcessing(true);

    try {
      await updateOrCreateDocument({
        data: {
          ...formatFormValuesForApi(formValues, formFieldBlocks),
          ...(step.setsValuesConfirmation && { userHasConfirmedValues: true }),
        },
        definition: documentDefinition,
        documentPath,
      });
      await performTransitionAndNavigate();
    } catch (err) {
      handleValidationErrorResponse(err, form.setError);

      setIsProcessing(false);
    }
  };

  return (
    <FormProvider {...form}>
      <StepRenderer
        blocks={blocks}
        isProcessing={isProcessing}
        onSubmit={form.handleSubmit(onSubmit)}
        step={step}
        workflowDataResolverDependencies={{ documentPath }}
      />
    </FormProvider>
  );
};
